import { Box, Button, Chip, Divider, Paper, Skeleton, Stack, Typography, alpha } from '@mui/material';
import { useState } from 'react';
import type { GroupMember, Meeting, Group } from '@penticton-dev-events/shared';
import { useAuth } from '../../../app/providers/AuthProvider';

export type GroupDetailProps = {
  readonly group: Group;
  readonly meetings: readonly Meeting[];
  readonly meetingsLoading?: boolean;
  readonly onJoin?: () => void;
  readonly joining?: boolean;
  readonly membership?: GroupMember | null;
  readonly membershipLoading?: boolean;
  readonly onMeetingClick?: (meeting: Meeting) => void;
  readonly onCancelMeeting?: (meetingId: string) => Promise<void>;
  readonly onRestoreMeeting?: (meetingId: string) => Promise<void>;
};

type GroupHeroBannerProps = {
  readonly group: Group;
  readonly onJoin?: () => void;
  readonly joining?: boolean;
  readonly membership?: GroupMember | null;
  readonly membershipLoading?: boolean;
};

const GroupHeroBanner = ({
  group,
  onJoin,
  joining = false,
  membership,
  membershipLoading = false,
}: GroupHeroBannerProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const membershipStatus = membership?.status ?? null;
  const joinDisabled =
    joining || membershipStatus === 'active' || membershipStatus === 'pending';

  const hasPoster = group.posterUrl && !error;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 300, sm: 350, md: 400 },
        overflow: 'hidden',
        borderRadius: 2,
        backgroundColor: 'grey.900',
      }}
    >
      {/* Background Image or Gradient */}
      {hasPoster ? (
        <>
          {loading && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          )}
          <Box
            component="img"
            src={group.posterUrl}
            alt={group.posterAlt || `${group.name} hero banner`}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: loading ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
        </>
      ) : (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: theme =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
          }}
        />
      )}

      {/* Dark overlay for better text contrast */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
        }}
      />

      {/* Content Overlay */}
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: { xs: 3, sm: 4, md: 5 },
          color: 'white',
        }}
      >
        <Stack spacing={2}>
          {/* Header with title and badges */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  textShadow: '0 2px 16px rgba(0,0,0,0.6)',
                  marginBottom: 1,
                }}
              >
                {group.name}
              </Typography>
              {user && (
                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.95,
                    textShadow: '0 1px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  Managed by {group.organizerName}
                </Typography>
              )}
              {typeof group.memberCount === 'number' && (
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    textShadow: '0 1px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
                </Typography>
              )}
            </Box>

            {/* Action buttons and chips */}
            <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1.5}>
              <Chip
                color={group.autoAcceptMembers ? 'success' : 'default'}
                label={group.autoAcceptMembers ? 'Auto accept enabled' : 'Manual approvals'}
                sx={{
                  backdropFilter: 'blur(8px)',
                  backgroundColor: theme =>
                    group.autoAcceptMembers
                      ? alpha(theme.palette.success.main, 0.9)
                      : alpha(theme.palette.grey[800], 0.8),
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              {onJoin && (
                <Button
                  disabled={joinDisabled}
                  onClick={onJoin}
                  size="large"
                  variant="contained"
                  sx={{
                    backdropFilter: 'blur(8px)',
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.9),
                    '&:hover': {
                      backgroundColor: theme => alpha(theme.palette.primary.dark, 0.95),
                    },
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  {membershipLoading
                    ? 'Checking membership…'
                    : membershipStatus === 'active'
                      ? 'Already a member'
                      : membershipStatus === 'pending'
                        ? 'Request sent'
                        : 'Request membership'}
                </Button>
              )}
              {membershipStatus && (
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.9,
                    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                  }}
                >
                  Membership status: {membershipStatus}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

const GroupDetail = ({
  group,
  meetings,
  meetingsLoading = false,
  onJoin,
  joining = false,
  membership,
  membershipLoading = false,
  onMeetingClick,
  onCancelMeeting,
  onRestoreMeeting,
}: GroupDetailProps) => {
  return (
    <Stack spacing={3}>
      {/* Hero Banner */}
      <GroupHeroBanner
        group={group}
        onJoin={onJoin}
        joining={joining}
        membership={membership}
        membershipLoading={membershipLoading}
      />

      {/* Group Description */}
      <Paper
        elevation={2}
        sx={theme => ({
          padding: 3,
          backgroundColor: 'background.paper',
          borderRadius: 2,
        })}
      >
        <Typography variant="h6" gutterBottom fontWeight={600}>
          About this group
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {group.summary ?? 'Organizers have not added a description yet.'}
        </Typography>
      </Paper>

      <Paper
        elevation={1}
        sx={theme => ({
          padding: 3,
          backgroundColor: 'background.paper',
          borderRadius: 2,
        })}
      >
        <Typography variant="h6" gutterBottom>
          Upcoming meetings
        </Typography>
        <Stack spacing={2}>
          {meetingsLoading ? (
            <Typography color="text.secondary">Loading meetings...</Typography>
          ) : meetings.length === 0 ? (
            <Typography color="text.secondary">No meetings scheduled yet.</Typography>
          ) : (
            meetings.map(meeting => (
              <Box key={meeting.id}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography
                      fontWeight={600}
                      component={onMeetingClick ? 'button' : 'div'}
                      onClick={onMeetingClick ? () => onMeetingClick(meeting) : undefined}
                      sx={
                        onMeetingClick
                          ? {
                              color: 'primary.main',
                              cursor: 'pointer',
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }
                          : {}
                      }
                    >
                      {meeting.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {new Date(meeting.startsAt).toLocaleString()} —{' '}
                      {new Date(meeting.endsAt).toLocaleTimeString()}
                    </Typography>
                  </Box>
                  {onCancelMeeting || onRestoreMeeting ? (
                    <Stack direction="row" gap={1}>
                      {meeting.status === 'scheduled' && onCancelMeeting ? (
                        <Button
                          color="warning"
                          onClick={() => {
                            void onCancelMeeting?.(meeting.id);
                          }}
                          size="small"
                          variant="outlined"
                        >
                          Cancel meeting
                        </Button>
                      ) : null}
                      {meeting.status === 'canceled' && onRestoreMeeting ? (
                        <Button
                          color="primary"
                          onClick={() => {
                            void onRestoreMeeting?.(meeting.id);
                          }}
                          size="small"
                          variant="outlined"
                        >
                          Reopen meeting
                        </Button>
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
                {meeting.status === 'canceled' ? (
                  <Typography color="error" variant="caption">
                    This meeting has been canceled.
                  </Typography>
                ) : null}
                <Typography variant="body2" mt={1}>
                  {meeting.description}
                </Typography>
              </Box>
            ))
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default GroupDetail;
